import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { useAuth } from '../AuthContext';

interface WatchlistContextType {
    watchlist: number[];
    addToWatchlist: (recapId: number) => Promise<void>;
    removeFromWatchlist: (recapId: number) => Promise<void>;
    isWatchlisted: (recapId: number) => boolean;
    loading: boolean;
}

const WatchlistContext = createContext<WatchlistContextType | undefined>(undefined);

export const WatchlistProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [watchlist, setWatchlist] = useState<number[]>([]);
    const [loading, setLoading] = useState(true);
    const { isAuthenticated } = useAuth();

    const fetchWatchlist = useCallback(async () => {
        if (!isAuthenticated) {
            setWatchlist([]);
            setLoading(false);
            return;
        }

        setLoading(true);
        try {
            const response = await fetch('/api/watchlist');
            if (!response.ok) {
                throw new Error('Failed to fetch watchlist');
            }
            const data: number[] = await response.json();
            setWatchlist(data);
        } catch (error) {
            console.error('Error fetching watchlist:', error);
            setWatchlist([]); // Clear watchlist on error
        } finally {
            setLoading(false);
        }
    }, [isAuthenticated]);

    useEffect(() => {
        fetchWatchlist();
    }, [fetchWatchlist]);

    const addToWatchlist = async (recapId: number) => {
        try {
            const response = await fetch('/api/watchlist', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ recapId }),
            });
            if (!response.ok) throw new Error('Failed to add to watchlist');
            setWatchlist(prev => [...prev, recapId]);
        } catch (error) {
            console.error('Error adding to watchlist:', error);
            throw error; // Re-throw to be handled by the component
        }
    };

    const removeFromWatchlist = async (recapId: number) => {
        try {
            const response = await fetch(`/api/watchlist/${recapId}`, {
                method: 'DELETE',
            });
            if (!response.ok) throw new Error('Failed to remove from watchlist');
            setWatchlist(prev => prev.filter(id => id !== recapId));
        } catch (error) {
            console.error('Error removing from watchlist:', error);
            throw error; // Re-throw to be handled by the component
        }
    };

    const isWatchlisted = (recapId: number) => watchlist.includes(recapId);

    return (
        <WatchlistContext.Provider value={{ watchlist, addToWatchlist, removeFromWatchlist, isWatchlisted, loading }}>
            {children}
        </WatchlistContext.Provider>
    );
};

export const useWatchlist = () => {
    const context = useContext(WatchlistContext);
    if (context === undefined) {
        throw new Error('useWatchlist must be used within a WatchlistProvider');
    }
    return context;
};
